import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { realtime as realtimeApi } from '@/api/endpoints';
import { useAuth } from '@/stores/auth';
import { useRealtime } from '@/stores/realtime';
import { RealtimeClient, gatewayUrl } from './client';

/**
 * Refetch when events were missed, and say so only until the refetch is done.
 *
 * Nothing cleared the flag before, and a gap did not refetch anything: the
 * Overview page claimed the figures "were refetched" and kept saying it for the
 * rest of the session.
 */
export function useResyncOnGap(): void {
  const resyncNeeded = useRealtime((state) => state.resyncNeeded);
  const resyncHandled = useRealtime((state) => state.resyncHandled);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!resyncNeeded) return;
    let cancelled = false;
    void queryClient.invalidateQueries().finally(() => {
      if (!cancelled) resyncHandled();
    });
    return () => {
      cancelled = true;
    };
  }, [resyncNeeded, resyncHandled, queryClient]);
}

/**
 * Owns the single realtime connection for the session.
 *
 * Mounted once, at the layout level. Every (re)connect refetches authoritative
 * state over REST, quietly: that is what makes "the socket is only a
 * notification channel" true in practice, and it is routine — the gateway ends
 * each connection when its session lifetime runs out. Only a gap in the
 * numbering while connected means events were missed.
 */
export function useRealtimeConnection(channels: string[]): void {
  const status = useAuth((state) => state.status);
  const setConnection = useRealtime((state) => state.setConnection);
  const ingest = useRealtime((state) => state.ingest);
  const connected = useRealtime((state) => state.connected);
  const queryClient = useQueryClient();
  useResyncOnGap();
  const channelsRef = useRef(channels);
  channelsRef.current = channels;

  useEffect(() => {
    if (status !== 'authenticated') return;

    const client = new RealtimeClient({
      url: gatewayUrl(),
      fetchTicket: async () => (await realtimeApi.ticket()).ticket,
      channels: () => channelsRef.current,
      onEvent: ingest,
      onState: setConnection,
      onResync: () => {
        connected();
        void queryClient.invalidateQueries();
      },
    });

    client.start();
    return () => client.stop();
  }, [status, ingest, setConnection, connected, queryClient]);
}
